import { IconProps } from '@euler/app/components/icons/types';
import { Input } from '@euler/components/form/Input';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { memo, useMemo } from 'react';
import { Text, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useOrderFlowContext } from '../../../../OrderFlowContext';
import { useTextInputRow } from '../../functions/useTextInputRow';
import { Cell } from '../components/Cell';
import { IconWrapper } from '../components/IconWrapper';
import { Row } from '../components/Row';

export const MileageRow = memo(
  ({
    required,
    defaultValue,
    ...props
  }: {
    required?: boolean;
    defaultValue?: number;
    disabled?: boolean;
  }) => {
    const {
      theme,
      lastServiceInfo$,
      formController: { form },
    } = useOrderFlowContext();

    const { iconColor, labelColor, placeholderColor } = theme;
    const { inputRef, inputStyle, onCellPress } = useTextInputRow();
    const [lastServiceInfo] = useBehaviorSubject(lastServiceInfo$);

    const lastServiceMileage = useMemo(() => {
      const lastRecord = lastServiceInfo?.serviceRecords[0];
      return lastRecord?.mileage;
    }, [lastServiceInfo?.serviceRecords]);

    const { rpx } = useResponsiveContext();

    return (
      <Row
        errorProp="mileage"
        icon={
          <IconWrapper>
            <RowIcon color={iconColor} />
          </IconWrapper>
        }
        onPress={onCellPress}
        {...props}
      >
        <Cell
          required={required}
          label="行驶里程"
          helpText={
            lastServiceMileage != null
              ? `上次服务里程: ${lastServiceMileage}公里`
              : undefined
          }
        >
          <View
            css={`
              flex-direction: row;
              justify-content: space-between;
              align-items: center;
            `}
          >
            <Input
              editable={!props.disabled}
              inputRef={inputRef}
              style={inputStyle}
              placeholderTextColor={placeholderColor}
              value={
                form.mileage.value != null &&
                (form.mileage.value > 0 || form.mileage.value === defaultValue)
                  ? String(form.mileage.value)
                  : ''
              }
              placeholder="请输入车辆里程"
              keyboardType="number-pad"
              errorMsg={form.mileage.error}
              onChangeText={x => form.mileage.update(Number(x))}
              returnKeyType="done"
              returnKeyLabel="确认"
              clearButtonMode="while-editing"
              onBlur={form.mileage.onBlur}
              containerStyle={{ flex: 1 }}
              alwaysShowError={form.mileage.value > 0}
            />
            <Text
              css={`
                font-family: ${FontFamily.NotoSans.Light};
                font-size: ${rpx(14)}px;
                color: ${labelColor};
              `}
            >
              公里
            </Text>
          </View>
        </Cell>
      </Row>
    );
  },
);

const RowIcon = memo(({ size, color, style, ...props }: IconProps) => {
  const { rpx } = useResponsiveContext();
  return (
    <Svg
      width={size ?? rpx(20)}
      height={size ?? rpx(20)}
      viewBox="0 0 20 20"
      fill="none"
      style={style}
      {...props}
    >
      <Path
        d="M11 2.062V4a1 1 0 1 1-2 0V1a1 1 0 0 1 1-1c5.523 0 10 4.477 10 10s-4.477 10-10 10S0 15.523 0 10a9.968 9.968 0 0 1 2.929-7.071 1 1 0 1 1 1.402 1.427A7.975 7.975 0 0 0 2 10a8 8 0 0 0 8 8 8 8 0 0 0 1-15.938Zm-1.723 8.63-2.07-2.07a1 1 0 1 1 1.415-1.415l2.07 2.07a1.75 1.75 0 1 1-1.414 1.414h-.001Z"
        fill={color ?? '#07F'}
      />
    </Svg>
  );
});
